import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import {
  DIAGNOSIS_FIELDS,
  diagnosisFromRow,
  hasDiagnosis,
  toDiagnosisColumns,
  type ServiceOrderDiagnosis,
} from '@/lib/serviceOrderDiagnosis';
import {
  ASSET_FIELDS,
  assetFromRow,
  hasAsset,
  toAssetColumns,
  type ServiceOrderAsset,
} from '@/lib/serviceOrderAsset';
import {
  buildServiceOrderHeaderColumns,
  serviceOrderHeaderFromRow,
} from '@/lib/serviceOrderHeader';
import { buildQuoteTotalsColumns, computeQuoteTotals, quoteItemLineTotal } from '@/lib/quoteTotals';
import { QUOTE_PAYMENT_METHODS, normalizePaymentMethods } from '@/lib/quotePaymentInfo';
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import { WARRANTY_UNITS, type WarrantyUnit } from '@/lib/quoteWarranty';
import { parseBRLInput, numberToBRLInput } from '@/lib/currencyInput';
import {
  ArrowLeft,
  User,
  Plus,
  Trash2,
  Package,
  FileText,
  Loader2,
  Stethoscope,
  Wrench,
  HardDrive,
  CreditCard,
  UserCog,
  Percent,
} from 'lucide-react';
import { toast } from 'sonner';
import { createUuid } from '@/lib/uuid';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { ClientSelector } from '@/components/quotes/ClientSelector';
import { CatalogSelector } from '@/components/quotes/CatalogSelector';
import { GeneralPhotosSection, type GeneralPhoto } from '@/components/quotes/GeneralPhotosSection';
import {
  useCreateServiceOrder,
  useUpdateServiceOrder,
  useServiceOrder,
  getOrderItems,
  getOrderPhotos,
  SERVICE_ORDER_STATUSES,
  type ServiceOrderStatus,
  type ServiceOrderInput,
} from '@/hooks/useServiceOrders';
import type { Client, Product } from '@/types/database';
import { productUnitCost } from '@/lib/cost';
import { serviceOrderItemUnitCost } from '@/lib/serviceOrderItems';
import { cacheBundles, getCachedBundles, getCachedCatalog } from '@/lib/offlineDataCache';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import {
  bundleLinesForServiceOrder,
  groupBundleItems,
  resolveBundleItems,
  type BundleItemRow,
} from '@/lib/productBundles';
import { plural } from '@/lib/plural';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { QuotePickerDialog } from '@/components/documents/QuotePickerDialog';

/**
 * PÁGINA · Ordem de serviço (criar e editar).
 *
 * Era um `Dialog` (`ServiceOrderFormModal`) aberto de quatro lugares. Virou rota
 * para poder usar o desenho oficial do app — hero sangrado + cartões + barra de
 * ações flutuante —, que é layout de PÁGINA: o hero usa `-mx-4 -mt-6` do
 * container do `MainLayout` e a barra se ancora na navegação inferior.
 *
 * Ganhos que vieram junto: o formulário mais longo do módulo deixou de disputar
 * altura com o `max-h-[90vh]` do diálogo, o botão de voltar do Android passou a
 * funcionar naturalmente, e o rascunho tem URL própria (compartilhável e
 * recarregável).
 *
 * ─── COMO OS CHAMADORES PASSAM CONTEXTO ─────────────────────────────────────
 *
 *  · `/ordens-servico/nova`                → em branco
 *  · `/ordens-servico/nova?orcamento=<id>` → semeia do orçamento (itens + fotos)
 *  · `/ordens-servico/nova` + state.seed   → semeia campos escalares (contrato)
 *  · `/ordens-servico/:id/editar`          → edita (carrega pelo id)
 *
 * O orçamento de origem vai na URL (é um id, sobrevive a recarregar a página); o
 * `seed` do contrato vai no state da navegação, porque é um objeto e não teria
 * sentido numa query string.
 */

interface EditItem {
  id: string;
  name: string;
  description?: string | null;
  quantity: number;
  unit_price: number;
  discount: number;
  discount_type: 'value' | 'percent';
  item_type: 'service' | 'product';
  unit?: string | null;
  image_url?: string | null;
  product_id?: string | null;
  barcode?: string | null;
  /**
   * SNAPSHOT do `products.cost` no momento em que o item entrou na OS. Não
   * aparece na tela — é dado de CMV/margem, gravado em
   * `service_order_items.unit_cost`. Precisa atravessar o formulário: se a
   * hidratação da edição perdesse o campo, salvar de novo uma OS antiga
   * gravaria `null` e apagaria o custo histórico dela em silêncio.
   * `null`/ausente = item avulso ou produto sem custo informado (NUNCA zero).
   */
  unit_cost?: number | null;
  /**
   * O serviço-kit que trouxe esta linha (grupo 10 / B1). `null` = item
   * escolhido diretamente. Atravessa o formulário e é gravado em
   * `service_order_items.bundle_parent_product_id`: é a marca que torna a
   * expansão idempotente e explica na tela de onde a linha veio.
   */
  bundle_parent_product_id?: string | null;
}

/** Campos escalares que um contrato assinado empurra para a OS derivada. */
export interface ServiceOrderSeed {
  client_id?: string | null;
  client_name?: string | null;
  client_phone?: string | null;
  client_address?: string | null;
  client_document?: string | null;
  title?: string | null;
}

/**
 * Rótulos CURTOS das pílulas de status.
 *
 * A grade de 4 colunas dá ~61px por pílula em 360px, e "Em andamento" não cabe
 * (o `truncate` comeria a palavra que distingue o status). É o mesmo motivo pelo
 * qual o gráfico de OS dos relatórios usa "Andam." (CLAUDE.md #84). O vocabulário
 * continua sendo o de `SERVICE_ORDER_STATUSES` — aqui só o rótulo encurta.
 */
const STATUS_PILL_LABELS: Record<ServiceOrderStatus, string> = {
  pending: 'Pendente',
  in_progress: 'Andamento',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

/** Pontinho colorido de cada status (mesmos tokens do resto do app). */
const STATUS_PILL_DOTS: Record<ServiceOrderStatus, string> = {
  pending: 'bg-status-pending',
  in_progress: 'bg-blue-500',
  completed: 'bg-status-completed',
  cancelled: 'bg-status-cancelled',
};

/**
 * Total da LINHA pela fonte única do dinheiro (#69).
 *
 * Existia aqui uma cópia da fórmula — a sexta do app —, e era exatamente a
 * situação que o hurdle descreve: com o desconto de cabeçalho e o acréscimo
 * entrando na conta, uma cópia divergente faria a tela, o PDF e o banco
 * mostrarem três totais diferentes, sem erro nenhum.
 */
const itemTotal = (it: EditItem): number => quoteItemLineTotal(it as never);

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/**
 * ISO (instante) → valor de um `<input type="datetime-local">`, no fuso LOCAL.
 *
 * ⚠️ `iso.slice(0, 16)` seria o reflexo óbvio e está ERRADO: o ISO é UTC, então
 * o campo mostraria 3 horas a mais e o usuário "corrigiria" um horário que ele
 * mesmo digitou certo (a família do #114).
 */
function toDateTimeLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** O caminho de volta: o valor do campo é hora LOCAL e vira o instante gravado. */
function fromDateTimeLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value); // sem 'Z' → interpretado no fuso local
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export default function ServiceOrderFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialQuoteId = searchParams.get('orcamento');
  const initialSeed = (location.state as { seed?: ServiceOrderSeed } | null)?.seed ?? null;

  const { user, profile } = useAuth();
  const { activeCompany } = useCompany();
  const { isOnline } = useNetworkStatus();
  const isEditing = Boolean(id);
  // Photos stay a PRO lever (same as quote photos), even though the OS module is free.
  const quotePhotos = useFeature('quote_photos');

  const { order, isLoading: loadingOrder } = useServiceOrder(id);
  const createOrder = useCreateServiceOrder();
  const updateOrder = useUpdateServiceOrder();

  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showCatalogSelector, setShowCatalogSelector] = useState(false);
  const [showQuotePicker, setShowQuotePicker] = useState(false);

  /**
   * Vínculos de kit (materiais inclusos). Cache primeiro e servidor depois,
   * como no formulário de orçamento: montar a OS a partir de um kit tem que
   * funcionar SEM rede — os materiais viram itens locais e o `product_id` deles
   * já é real (o vínculo é online-only, então nunca é `offline_`).
   */
  const [bundleRows, setBundleRows] = useState<BundleItemRow[]>([]);

  useEffect(() => {
    if (!user) return;

    const cached = getCachedBundles(user.id);
    if (cached) setBundleRows(cached as BundleItemRow[]);

    if (!isOnline || !activeCompany) return;

    let cancelled = false;
    supabase
      .from('product_bundle_items')
      .select('*')
      .eq('company_id', activeCompany.id)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        setBundleRows(data as BundleItemRow[]);
        cacheBundles(user.id, data);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, isOnline, activeCompany?.id]);

  const bundlesByParent = useMemo(() => groupBundleItems(bundleRows), [bundleRows]);

  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientDocument, setClientDocument] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ServiceOrderStatus>('pending');
  /**
   * Imprimir o número da OS no PDF. A numeração (`sequence_id`) sempre existiu e
   * sempre foi por empresa — o que não existia era o interruptor: `show_sequence_id`
   * nascia `false` e não havia como ligá-lo, então o número NUNCA saía no
   * documento. Quem numera OS para o cliente (assistência técnica, oficina)
   * ficava sem o campo mais básico do papel.
   */
  const [showSequenceId, setShowSequenceId] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  /**
   * Previsão de ENTREGA, com hora (`datetime-local`).
   *
   * ⚠️ Não confundir com `scheduledDate`: aquela é date-only guardada à
   * meia-noite UTC porque a Agenda a lê assim (`localDateFromIsoDatePart`), e
   * pôr hora nela quebraria o agrupamento por dia depois das 21h. Esta é um
   * instante de verdade e não entra no calendário.
   */
  const [expectedDate, setExpectedDate] = useState('');
  /** Diagnóstico técnico — ver `src/lib/serviceOrderDiagnosis.ts`. */
  const [diagnosisForm, setDiagnosisForm] = useState<ServiceOrderDiagnosis>({
    defect: null,
    diagnosis: null,
    solution: null,
  });
  /** Equipamento/objeto — ver `src/lib/serviceOrderAsset.ts`. */
  const [assetForm, setAssetForm] = useState<ServiceOrderAsset>({
    assetType: null,
    assetBrand: null,
    assetIdentifier: null,
    assetAccessories: null,
    assetCondition: null,
  });
  const [attendantName, setAttendantName] = useState('');
  const [technicianName, setTechnicianName] = useState('');
  /** `null` = não informado (legado). `[]` = escolha explícita de nenhuma (#73). */
  const [paymentMethods, setPaymentMethods] = useState<string[] | null>(null);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [warrantyAmount, setWarrantyAmount] = useState('');
  const [warrantyUnit, setWarrantyUnit] = useState<WarrantyUnit>('days');
  const [termsConditions, setTermsConditions] = useState('');
  /** Ajustes do valor final — mesmas colunas e mesma conta do orçamento (#69). */
  const [discountInput, setDiscountInput] = useState('');
  const [discountType, setDiscountType] = useState<'value' | 'percent'>('value');
  const [shippingInput, setShippingInput] = useState('');
  const [items, setItems] = useState<EditItem[]>([]);
  const [photos, setPhotos] = useState<GeneralPhoto[]>([]);
  /**
   * Accordion do diagnóstico: CONTROLADO, não `defaultValue`.
   *
   * `defaultValue` é lido só na primeira renderização — e nesta página a OS
   * chega DEPOIS (o modal antigo só montava com o dado em mãos). Uma OS com
   * diagnóstico preenchido abriria com o bloco fechado, escondendo justamente o
   * que a assistência técnica veio ver. Pego pelo teste migrado.
   */
  const [diagnosisOpen, setDiagnosisOpen] = useState<string | undefined>(undefined);
  /** Mesmo motivo do accordion de diagnóstico: CONTROLADO, não `defaultValue`. */
  const [assetOpen, setAssetOpen] = useState<string | undefined>(undefined);

  /**
   * A hidratação roda UMA vez, quando a OS chega.
   *
   * No modal a dependência era `open`; aqui a página já nasce montada e o dado
   * vem de forma assíncrona. Sem o marcador, cada refetch da lista (o hook lê
   * `useServiceOrders`) reidrataria o formulário e apagaria o que o usuário
   * acabou de digitar.
   */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isEditing || hydratedRef.current || !order) return;
    hydratedRef.current = true;

    setClientId(order.client_id ?? null);
    setClientName(order.client_name ?? '');
    setClientPhone(order.client_phone ?? '');
    setClientAddress(order.client_address ?? '');
    setClientDocument(order.client_document ?? '');
    setQuoteId(order.quote_id ?? null);
    setTitle(order.title ?? '');
    setDescription(order.description ?? '');
    setStatus((order.status ?? 'pending') as ServiceOrderStatus);
    setShowSequenceId(order.show_sequence_id === true);
    setScheduledDate(order.scheduled_date ? String(order.scheduled_date).slice(0, 10) : '');
    // LEITURA dos blocos novos. Perder um campo aqui é tão destrutivo quanto
    // perdê-lo na escrita: o próximo save grava `null` por cima e o dado some
    // sozinho, sem erro nenhum (CLAUDE.md #70).
    setAssetForm(assetFromRow(order as any));
    if (hasAsset(assetFromRow(order as any))) setAssetOpen('equipamento');
    const header = serviceOrderHeaderFromRow(order as any);
    setClientEmail(header.clientEmail ?? '');
    setExpectedDate(toDateTimeLocalInput(header.expectedDate));
    setAttendantName(header.attendantName ?? '');
    setTechnicianName(header.technicianName ?? '');
    setPaymentMethods(header.paymentMethods);
    setPaymentTerms(header.paymentTerms ?? '');
    setWarrantyAmount(header.warrantyAmount == null ? '' : String(header.warrantyAmount));
    setWarrantyUnit(header.warrantyUnit ?? 'days');
    setTermsConditions(header.termsConditions ?? '');
    // Dinheiro: o desconto em VALOR é moeda e entra pelo par
    // `parseBRLInput`/`numberToBRLInput`; em PERCENTUAL é número puro, e passá-lo
    // pelo formatador de moeda faria 10% virar "0,10" (#71/#153a).
    const rawDiscount = Number(order.discount ?? 0);
    setDiscountType(order.discount_type === 'percent' ? 'percent' : 'value');
    setDiscountInput(
      rawDiscount > 0
        ? order.discount_type === 'percent'
          ? String(rawDiscount)
          : numberToBRLInput(rawDiscount)
        : '',
    );
    const rawShipping = Number(order.shipping ?? 0);
    setShippingInput(rawShipping > 0 ? numberToBRLInput(rawShipping) : '');
    // LEITURA do diagnóstico. Perder o campo aqui é tão destrutivo quanto
    // perdê-lo na escrita: o próximo save gravaria null e apagaria o texto
    // sem erro nenhum (CLAUDE.md #70).
    // Chamada REPETIDA de propósito (CLAUDE.md #75): o guard de varredura de
    // `serviceOrderDiagnosis.test.ts` procura `setDiagnosisForm(diagnosisFromRow(`
    // literalmente, e extrair para uma variável o desliga — trocaria a proteção
    // contra perder o diagnóstico na leitura por uma linha a menos.
    // `diagnosisFromRow` é um mapeador puro; chamá-lo duas vezes não custa nada.
    setDiagnosisForm(diagnosisFromRow(order as any));
    if (hasDiagnosis(diagnosisFromRow(order as any))) setDiagnosisOpen('diagnostico');
    setItems(
      getOrderItems(order).map((it: any) => ({
        id: it.id ? String(it.id) : createUuid(),
        name: it.name ?? '',
        description: it.description ?? '',
        quantity: Number(it.quantity ?? 1),
        unit_price: Number(it.unit_price ?? 0),
        discount: Number(it.discount ?? 0),
        discount_type: (it.discount_type as 'value' | 'percent') ?? 'value',
        item_type: (it.item_type as 'service' | 'product') ?? 'service',
        unit: it.unit ?? null,
        image_url: it.image_url ?? null,
        product_id: it.product_id ?? null,
        barcode: it.barcode ?? null,
        // Snapshot carregado do que foi GRAVADO, nunca relido do catálogo:
        // reeditar uma OS antiga não pode reescrever o custo histórico dela.
        unit_cost: serviceOrderItemUnitCost(it),
        // O guard cobra a coluna nos DOIS sentidos (#70): perdê-la na LEITURA
        // faz o próximo save gravar null e a marca de origem sumir.
        bundle_parent_product_id: it.bundle_parent_product_id ?? null,
      })),
    );
    setPhotos(
      getOrderPhotos(order).map((p: any) => ({
        id: p.id ? String(p.id) : createUuid(),
        imageUrl: p.image_url ?? p.imageUrl ?? '',
        title: p.title ?? undefined,
        description: p.description ?? undefined,
      })),
    );
  }, [isEditing, order]);

  // Fills the form from a quote + its items/photos. Shared by the manual picker
  // and the `?orcamento=` seed path so both stay in sync.
  const applyQuoteSeed = (quote: any, quoteItems: any[], quotePhotosRows: any[]) => {
    setQuoteId(quote.id);
    setClientId(quote.client_id ?? null);
    setClientName(quote.client_name ?? '');
    setClientPhone(quote.client_phone ?? '');
    setClientAddress(quote.client_address ?? '');
    setClientDocument(quote.client_document ?? '');
    if (quote.title) setTitle(quote.title);
    // OS AGORA HERDA OS AJUSTES DE VALOR DO ORÇAMENTO — e isto conserta um
    // defeito silencioso: o total da OS era só a soma dos itens, então gerar uma
    // OS de um orçamento com desconto de cabeçalho ou frete produzia um
    // documento com um total DIFERENTE do que o cliente aprovou.
    const seedDiscount = Number(quote.discount ?? 0);
    setDiscountType(quote.discount_type === 'percent' ? 'percent' : 'value');
    setDiscountInput(
      seedDiscount > 0
        ? quote.discount_type === 'percent'
          ? String(seedDiscount)
          : numberToBRLInput(seedDiscount)
        : '',
    );
    const seedShipping = Number(quote.shipping ?? 0);
    setShippingInput(seedShipping > 0 ? numberToBRLInput(seedShipping) : '');
    // Condições comerciais combinadas na proposta seguem para a execução.
    setPaymentMethods(normalizePaymentMethods(quote.payment_methods));
    setPaymentTerms(quote.payment_terms ?? '');
    if (quote.warranty_amount != null && quote.warranty_unit) {
      setWarrantyAmount(String(quote.warranty_amount));
      setWarrantyUnit(quote.warranty_unit as WarrantyUnit);
    }
    setTermsConditions(quote.terms_conditions ?? '');
    setItems(
      (quoteItems ?? []).map((it: any) => ({
        id: createUuid(),
        name: it.name ?? '',
        description: it.description ?? '',
        quantity: Number(it.quantity ?? 1),
        unit_price: Number(it.unit_price ?? 0),
        discount: Number(it.discount ?? 0),
        discount_type: (it.discount_type as 'value' | 'percent') ?? 'value',
        item_type: (it.item_type as 'service' | 'product') ?? 'service',
        unit: it.unit ?? null,
        image_url: it.image_url ?? null,
        product_id: it.product_id ?? null,
        barcode: it.barcode ?? null,
        // A OS nascida de um orçamento HERDA o snapshot da linha do orçamento —
        // é o custo daquele momento. Reler `products.cost` aqui faria a OS e o
        // orçamento que a originou divergirem no CMV.
        unit_cost: serviceOrderItemUnitCost(it),
        // `quote_items` NÃO tem coluna de origem de kit — o material herdado
        // chega como item comum. É por isso que a regra de idempotência olha o
        // `product_id` e não a marca (ver `bundleLinesForServiceOrder`).
        bundle_parent_product_id: null,
      })),
    );
    setPhotos(
      (quotePhotosRows ?? [])
        .filter((p: any) => p.image_url ?? p.imageUrl)
        .map((p: any) => ({
          id: createUuid(),
          imageUrl: p.image_url ?? p.imageUrl,
          title: p.title ?? undefined,
          description: p.description ?? undefined,
        })),
    );
  };

  /**
   * Atendente de uma OS NOVA: quem está logado. É a resposta certa na esmagadora
   * maioria dos casos (o público do app é MEI/autônomo) e continua editável.
   *
   * Só na criação e só enquanto o campo estiver vazio: rodar isso na edição
   * sobrescreveria o atendente REGISTRADO de uma OS antiga com o nome de quem
   * está abrindo a tela hoje — reescrevendo história, que é o que o snapshot de
   * custo (#61) existe para impedir do outro lado.
   */
  const attendantSeededRef = useRef(false);
  useEffect(() => {
    if (isEditing || attendantSeededRef.current) return;
    const name = (profile?.full_name ?? '').trim();
    if (!name) return;
    attendantSeededRef.current = true;
    setAttendantName((prev) => (prev.trim() ? prev : name));
  }, [isEditing, profile?.full_name]);

  /**
   * Semeia o formulário de CRIAÇÃO a partir do orçamento da URL (ou dos campos
   * escalares do contrato). Roda uma vez só — o marcador impede que uma
   * renderização nova redisparasse a busca e sobrescrevesse a edição em curso.
   */
  const seededRef = useRef(false);
  useEffect(() => {
    if (isEditing || seededRef.current) return;
    if (!initialQuoteId && !initialSeed) return;
    seededRef.current = true;

    let cancelled = false;
    (async () => {
      if (initialQuoteId) {
        const { data: quote } = await supabase
          .from('quotes')
          .select('*')
          .eq('id', initialQuoteId)
          .maybeSingle();
        if (cancelled || !quote) return;
        const [itemsRes, photosRes] = await Promise.all([
          supabase.from('quote_items').select('*').eq('quote_id', initialQuoteId).order('position', { ascending: true }),
          supabase.from('quote_photos').select('*').eq('quote_id', initialQuoteId).order('position', { ascending: true }),
        ]);
        if (cancelled) return;
        applyQuoteSeed(quote, (itemsRes.data ?? []) as any[], (photosRes.data ?? []) as any[]);
      } else if (initialSeed) {
        if (cancelled) return;
        setClientId(initialSeed.client_id ?? null);
        setClientName(initialSeed.client_name ?? '');
        setClientPhone(initialSeed.client_phone ?? '');
        setClientAddress(initialSeed.client_address ?? '');
        setClientDocument(initialSeed.client_document ?? '');
        if (initialSeed.title) setTitle(initialSeed.title);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `initialSeed` vem do state da navegação e é lido uma vez, na entrada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, initialQuoteId]);

  /**
   * O dinheiro do documento, pela MESMA função do orçamento: itens − descontos
   * de linha − desconto do documento + acréscimo, arredondado em centavos.
   */
  const discountValue = useMemo(
    () => (discountType === 'percent' ? Number(discountInput.replace(',', '.')) || 0 : parseBRLInput(discountInput)),
    [discountInput, discountType],
  );
  const shippingValue = useMemo(() => parseBRLInput(shippingInput), [shippingInput]);
  const totals = useMemo(
    () =>
      computeQuoteTotals({
        items: items as never,
        discount: discountValue,
        discountType,
        shipping: shippingValue,
      }),
    [items, discountValue, discountType, shippingValue],
  );
  const total = totals.total;

  const handleSelectClient = (client: Client) => {
    setClientId(client.id);
    setClientName(client.name ?? '');
    setClientPhone(client.phone ?? '');
    setClientAddress(client.address ?? '');
    setClientDocument((client as any).document ?? '');
    setClientEmail((client as any).email ?? '');
  };

  const addFromCatalog = (products: Product[]) => {
    // O catálogo é lido do cache AGORA, não guardado em estado na montagem: o
    // `CatalogSelector` acabou de buscar e cachear a lista, então um estado
    // carregado no mount estaria vazio no primeiro uso depois do login — e o
    // kit simplesmente não expandiria, sem erro nenhum (mesma armadilha #65).
    const catalogProducts = (user ? getCachedCatalog(user.id) : null) ?? products;

    setItems((prev) => {
      const kept = prev.filter((it) => it.name.trim());
      const newItems: EditItem[] = [];
      // Produtos JÁ presentes na OS — inclusive os herdados de um orçamento,
      // que chegam sem marca de origem. É esta lista que impede a dupla
      // materialização (ver `bundleLinesForServiceOrder`).
      const existingProductIds = new Set(
        [...kept, ...newItems]
          .map((it) => String(it.product_id ?? ''))
          .filter(Boolean),
      );
      let bundledCount = 0;

      for (const p of products) {
        newItems.push({
          id: createUuid(),
          name: p.name,
          description: p.description ?? '',
          quantity: 1,
          unit_price: (p as any).price ?? 0,
          discount: 0,
          discount_type: 'value',
          item_type: (p as any).type === 'product' ? 'product' : 'service',
          unit: (p as any).unit ?? 'un',
          image_url: (p as any).image_url ?? null,
          product_id: p.id,
          barcode: (p as any).barcode ?? null,
          // ÚNICO momento em que o catálogo é consultado: o custo é congelado
          // aqui, ao ADICIONAR. Depois disso a linha só propaga o valor.
          unit_cost: productUnitCost(p),
          bundle_parent_product_id: null,
        });
        existingProductIds.add(String(p.id));

        // MATERIAIS INCLUSOS: o serviço-kit traz os materiais como itens
        // PRÓPRIOS, cada um com o seu `product_id` — é isso que faz a baixa de
        // estoque funcionar de graça (`toDeductionLines` consolida por
        // product_id) sem tocar no módulo de Estoque.
        const bundle = bundlesByParent.get(String(p.id));
        if (!bundle || bundle.length === 0) continue;

        const resolved = resolveBundleItems(bundle, catalogProducts);
        for (const line of bundleLinesForServiceOrder(resolved, {
          parentId: String(p.id),
          existingProductIds,
        })) {
          newItems.push({ id: createUuid(), ...line });
          existingProductIds.add(String(line.product_id));
          bundledCount += 1;
        }
      }

      if (bundledCount > 0) {
        // Linhas que aparecem sozinhas precisam ser anunciadas, senão parecem bug.
        toast.info(
          `${plural(bundledCount, 'material incluso foi adicionado', 'materiais inclusos foram adicionados')} à ordem de serviço.`,
        );
      }

      return [...kept, ...newItems];
    });
  };

  const addManualItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: createUuid(),
        name: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        discount: 0,
        discount_type: 'value',
        item_type: 'service',
        unit: 'un',
      },
    ]);
  };

  const updateItem = (id: string, patch: Partial<EditItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (itemId: string) => setItems((prev) => prev.filter((it) => it.id !== itemId));

  const handleSubmit = async () => {
    if (!clientName.trim() && !title.trim()) {
      toast.error('Informe ao menos um cliente ou um título para a ordem de serviço.');
      return;
    }

    const payload: ServiceOrderInput = {
      quote_id: quoteId,
      client_id: clientId,
      client_name: clientName.trim() || null,
      client_phone: clientPhone.trim() || null,
      client_address: clientAddress.trim() || null,
      client_document: clientDocument.trim() || null,
      title: title.trim() || null,
      description: description.trim() || null,
      status,
      show_sequence_id: showSequenceId,
      scheduled_date: scheduledDate ? new Date(scheduledDate).toISOString() : null,
      // As 3 colunas do diagnóstico de uma vez (#33/#62).
      ...toDiagnosisColumns(diagnosisForm),
      // As 5 do equipamento e as 9 do cabeçalho, pelo mesmo motivo.
      ...toAssetColumns(assetForm),
      ...buildServiceOrderHeaderColumns({
        clientEmail,
        expectedDate: fromDateTimeLocalInput(expectedDate),
        attendantName,
        technicianName,
        paymentMethods,
        paymentTerms,
        warrantyAmount,
        warrantyUnit,
        termsConditions,
      }),
      // E as 4 do dinheiro, pela fonte única (#69).
      ...buildQuoteTotalsColumns({
        items: items as never,
        discount: discountValue,
        discountType,
        shipping: shippingValue,
      }),
      items: items
        .filter((it) => it.name.trim())
        .map((it, i) => ({
          name: it.name.trim(),
          description: it.description ?? null,
          quantity: Number(it.quantity || 0),
          unit_price: Number(it.unit_price || 0),
          discount: Number(it.discount || 0),
          discount_type: it.discount_type,
          item_type: it.item_type,
          unit: it.unit ?? null,
          image_url: it.image_url ?? null,
          product_id: it.product_id ?? null,
          barcode: it.barcode ?? null,
          // Snapshot do custo (armadilha #33/#62): este payload é montado por
          // LISTA EXPLÍCITA de colunas, e esquecer a coluna gravaria a OS com
          // sucesso e o custo em branco — sem erro, sem toast, sem log.
          unit_cost: serviceOrderItemUnitCost(it),
          // Origem do kit: mesma lista explícita, mesmo risco (#33/#62). Sem
          // esta linha a OS salva com sucesso e a marca some — e a expansão
          // deixa de ser idempotente ao reabrir.
          bundle_parent_product_id: it.bundle_parent_product_id ?? null,
          total: Math.max(0, itemTotal(it)),
          position: i,
        })),
      photos: photos.map((p, i) => ({
        image_url: p.imageUrl,
        title: p.title ?? null,
        description: p.description ?? null,
        position: i,
      })),
    };

    try {
      if (isEditing) {
        // Editar atualiza só os campos escalares (itens e fotos são editados na
        // criação). O `updates` sai do MESMO payload da criação, sem uma segunda
        // lista explícita: era daqui que o diagnóstico já tinha ficado de fora
        // uma vez, e com 20 colunas a chance de repetir isso é certeza. Campo
        // presente na hidratação e ausente no update = save com sucesso e valor
        // que nunca muda (CLAUDE.md #139).
        //
        // ⚠️ A ASSINATURA DO CLIENTE NÃO ESTÁ NO PAYLOAD, de propósito: ela é
        // coletada na tela de detalhe, na entrega, e este formulário não a
        // conhece. Um `client_signature: null` aqui apagaria a prova de
        // recebimento a cada edição da OS.
        const { items: _items, photos: _photos, ...scalarUpdates } = payload;
        await updateOrder.mutateAsync({ id: order.id, updates: scalarUpdates });
        toast.success('Ordem de serviço atualizada.');
      } else {
        const res = await createOrder.mutateAsync(payload);
        toast.success(
          (res as any)?.offline
            ? 'Ordem de serviço salva localmente.'
            : 'Ordem de serviço criada.',
        );
      }
      // Volta para de onde veio (lista, orçamento, contrato ou a própria OS) —
      // o mesmo destino que o fechar do diálogo produzia.
      navigate(-1);
    } catch {
      // toast handled in the mutation onError
    }
  };

  const saving = createOrder.isPending || updateOrder.isPending;

  // Edição de uma OS que ainda não chegou (cache frio, link direto).
  if (isEditing && !order && loadingOrder) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          <ServiceOrderHero
            isEditing
            total={0}
            itemCount={0}
            clientName=""
            status="pending"
            onStatusChange={() => {}}
            onBack={() => navigate(-1)}
          />
          <div className="relative z-10 -mt-9 flex justify-center rounded-2xl border border-border/60 bg-card py-16 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (isEditing && !order) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h1 className="text-2xl font-bold text-muted-foreground">
            Ordem de serviço não encontrada
          </h1>
          <Button3D className="mt-6" onClick={() => navigate('/ordens-servico')}>
            Ver ordens de serviço
          </Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        <ServiceOrderHero
          isEditing={isEditing}
          total={total}
          itemCount={items.length}
          clientName={clientName}
          status={status}
          onStatusChange={setStatus}
          onBack={() => navigate(-1)}
        />

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-3">
          {!isEditing && (
            <Button
              type="button"
              variant="outline"
              className="h-11 w-full justify-start rounded-2xl bg-card"
              onClick={() => setShowQuotePicker(true)}
            >
              <FileText className="mr-2 h-4 w-4 text-primary" />
              Criar a partir de um orçamento
            </Button>
          )}

          {/* CLIENTE */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel icon={User} htmlFor="os-client">
                Cliente
              </FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="os-client"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Nome do cliente"
                  className={FIELD_INPUT}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl"
                  onClick={() => setShowClientSelector(true)}
                  aria-label="Escolher cliente do cadastro"
                >
                  <User className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="os-phone">Telefone</FieldLabel>
                <Input
                  id="os-phone"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                  className={FIELD_INPUT}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="os-document">CPF/CNPJ</FieldLabel>
                <Input
                  id="os-document"
                  value={clientDocument}
                  onChange={(e) => setClientDocument(e.target.value)}
                  placeholder="Documento"
                  className={FIELD_INPUT}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="os-email">E-mail</FieldLabel>
              <Input
                id="os-email"
                type="email"
                inputMode="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="cliente@email.com"
                className={FIELD_INPUT}
              />
            </div>
          </div>

          {/* SERVIÇO */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <div className="space-y-1.5">
              <FieldLabel icon={Wrench} htmlFor="os-title">
                Título
              </FieldLabel>
              <Input
                id="os-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex.: Instalação elétrica"
                className={FIELD_INPUT}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="os-description">Descrição</FieldLabel>
              <Textarea
                id="os-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Detalhes do serviço"
                rows={3}
                className="rounded-xl bg-background"
              />
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="os-scheduled">Agendada para</FieldLabel>
                <Input
                  id="os-scheduled"
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className={FIELD_INPUT}
                />
                <p className="text-[11px] text-muted-foreground">Entra na Agenda.</p>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="os-expected">Previsão de entrega</FieldLabel>
                <Input
                  id="os-expected"
                  type="datetime-local"
                  value={expectedDate}
                  onChange={(e) => setExpectedDate(e.target.value)}
                  className={FIELD_INPUT}
                />
                <p className="text-[11px] text-muted-foreground">
                  O prazo prometido ao cliente. Sai no documento.
                </p>
              </div>
            </div>

            {/* Número da OS: a numeração por empresa sempre existiu; o que
                faltava era poder imprimi-la. */}
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background px-3 py-2.5">
              <div className="min-w-0">
                <FieldLabel htmlFor="os-show-sequence">Mostrar nº da OS no PDF</FieldLabel>
                <p className="text-[11px] text-muted-foreground">
                  {order?.sequence_id
                    ? `Esta ordem é a nº ${String(order.sequence_id).padStart(4, '0')}.`
                    : 'O número é gerado ao salvar, por empresa.'}
                </p>
              </div>
              <Switch
                id="os-show-sequence"
                checked={showSequenceId}
                onCheckedChange={setShowSequenceId}
              />
            </div>
          </div>

          {/*
            EQUIPAMENTO / OBJETO — recolhível e fechado quando vazio, como o
            diagnóstico. Assistência técnica, oficina e TI preenchem sempre;
            instalação, limpeza e jardinagem não preenchem nunca, e um bloco
            sempre aberto empurraria itens e total para fora da tela.
          */}
          <Accordion type="single" collapsible value={assetOpen} onValueChange={setAssetOpen}>
            <AccordionItem value="equipamento" className={cn(CONTENT_CARD, 'overflow-hidden px-4')}>
              <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4 shrink-0 text-primary" />
                  <span className="text-left">
                    Equipamento / objeto
                    <span className="ml-1 font-normal text-muted-foreground">(opcional)</span>
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                {ASSET_FIELDS.map((field) => (
                  <div key={field.key} className="space-y-1.5">
                    <FieldLabel htmlFor={`os-${field.column}`}>{field.label}</FieldLabel>
                    {field.key === 'assetAccessories' || field.key === 'assetCondition' ? (
                      <Textarea
                        id={`os-${field.column}`}
                        value={assetForm[field.key] ?? ''}
                        onChange={(e) =>
                          setAssetForm((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        placeholder={field.placeholder}
                        rows={2}
                        className="rounded-xl bg-background"
                      />
                    ) : (
                      <Input
                        id={`os-${field.column}`}
                        value={assetForm[field.key] ?? ''}
                        onChange={(e) =>
                          setAssetForm((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        placeholder={field.placeholder}
                        className={FIELD_INPUT}
                      />
                    )}
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/*
            Diagnóstico técnico — bloco recolhível, fechado quando a OS não tem
            nada preenchido. Assistência técnica usa os três campos toda vez;
            instalação/limpeza não usa nenhum, e um bloco sempre aberto
            empurraria itens e total para fora da tela.
          */}
          <Accordion
            type="single"
            collapsible
            value={diagnosisOpen}
            onValueChange={setDiagnosisOpen}
          >
            <AccordionItem value="diagnostico" className={cn(CONTENT_CARD, 'overflow-hidden px-4')}>
              <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 shrink-0 text-primary" />
                  <span className="text-left">
                    Diagnóstico técnico
                    <span className="ml-1 font-normal text-muted-foreground">(opcional)</span>
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                {DIAGNOSIS_FIELDS.map((field) => (
                  <div key={field.key} className="space-y-1.5">
                    <FieldLabel>{field.label}</FieldLabel>
                    <Textarea
                      value={diagnosisForm[field.key] ?? ''}
                      onChange={(e) =>
                        setDiagnosisForm((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                      placeholder={field.placeholder}
                      rows={2}
                      className="rounded-xl bg-background"
                    />
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* ITENS — editáveis só na criação. Editar uma OS existente atualiza os
              campos escalares; para trocar itens, crie uma nova OS. */}
          {isEditing ? (
            <p className={cn(CONTENT_CARD, 'px-4 py-3 text-xs text-muted-foreground')}>
              Itens e fotos não são editáveis aqui. Para alterá-los, crie uma nova ordem de serviço.
            </p>
          ) : (
            <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
              <div className="flex items-center justify-between gap-2">
                <FieldLabel icon={Package}>Itens</FieldLabel>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowCatalogSelector(true)}>
                    <Package className="mr-1.5 h-3.5 w-3.5" />
                    Catálogo
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={addManualItem}>
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Item
                  </Button>
                </div>
              </div>
              {items.length === 0 && (
                <p className="py-2 text-center text-sm text-muted-foreground">Nenhum item adicionado</p>
              )}
              {items.map((it) => (
                <div key={it.id} className="space-y-2 rounded-xl border border-dashed border-primary/40 p-3">
                  <div className="flex items-center gap-2">
                    <Input
                      value={it.name}
                      onChange={(e) => updateItem(it.id, { name: e.target.value })}
                      placeholder="Nome do item"
                      className={FIELD_INPUT}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => removeItem(it.id)}
                      aria-label={`Remover ${it.name || 'item'}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  {/* Linha que apareceu sozinha precisa dizer de onde veio —
                      senão o usuário lê como item fantasma. */}
                  {it.bundle_parent_product_id && (
                    <p className="text-[11px] text-muted-foreground">
                      Material incluso em{' '}
                      {items.find((other) => other.product_id === it.bundle_parent_product_id)?.name
                        ?? 'um serviço'}
                    </p>
                  )}
                  <div className="grid grid-cols-2 items-start gap-3">
                    <div className="space-y-1.5">
                      <FieldLabel>Qtd</FieldLabel>
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={it.quantity}
                        onChange={(e) => updateItem(it.id, { quantity: Number(e.target.value) })}
                        className={FIELD_INPUT}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <FieldLabel>Preço unit.</FieldLabel>
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={it.unit_price}
                        onChange={(e) => updateItem(it.id, { unit_price: Number(e.target.value) })}
                        className={FIELD_INPUT}
                      />
                    </div>
                  </div>
                  <p className="text-right text-xs text-muted-foreground">
                    Subtotal: {formatBRL(Math.max(0, itemTotal(it)))}
                  </p>
                </div>
              ))}

              {/* Photos */}
              <GeneralPhotosSection
                photos={photos}
                onChange={setPhotos}
                isProUser={quotePhotos.allowed}
                onProFeatureClick={() => quotePhotos.require()}
              />
            </div>
          )}

          {/*
            AJUSTES DO VALOR FINAL — desconto do documento e acréscimo.
            Fica FORA do bloco de itens de propósito: itens só são editáveis na
            criação, mas renegociar desconto depois de a OS existir é rotina.
            A conta é a do orçamento (#69): desconto de item primeiro, desconto
            do documento sobre o subtotal já líquido, acréscimo por último e sem
            desconto.
          */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <FieldLabel icon={Percent}>Ajustes do valor</FieldLabel>
            <div className="grid grid-cols-2 items-start gap-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="os-discount">Desconto</FieldLabel>
                <div className="flex gap-1.5">
                  <Input
                    id="os-discount"
                    type="text"
                    inputMode="decimal"
                    value={discountInput}
                    // O texto é guardado cru; quem interpreta é `discountValue`,
                    // e ele muda de parser conforme o tipo: em R$ é MOEDA
                    // (`parseBRLInput`), em % é número puro. Passar 10% pelo
                    // parser de moeda faria o desconto virar R$ 0,10 (#71).
                    onChange={(e) => setDiscountInput(e.target.value)}
                    placeholder={discountType === 'percent' ? '10' : '0,00'}
                    className={cn(FIELD_INPUT, 'flex-1')}
                  />
                  <div className="flex shrink-0 gap-1">
                    {(['value', 'percent'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        aria-pressed={discountType === type}
                        onClick={() => setDiscountType(type)}
                        className={cn(
                          'w-9 rounded-xl border text-xs font-semibold transition-colors',
                          discountType === type
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input bg-background text-muted-foreground',
                        )}
                      >
                        {type === 'value' ? 'R$' : '%'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="os-shipping">Acréscimo / frete</FieldLabel>
                <Input
                  id="os-shipping"
                  type="text"
                  inputMode="decimal"
                  value={shippingInput}
                  onChange={(e) => setShippingInput(e.target.value)}
                  placeholder="0,00"
                  className={FIELD_INPUT}
                />
              </div>
            </div>

            {/* A decomposição só aparece quando há o que decompor — a mesma
                regra do PDF e da página pública, para os três concordarem. */}
            {(totals.descontoItens > 0 || totals.descontoCabecalho > 0 || totals.frete > 0) && (
              <div className="space-y-1 rounded-xl bg-muted/40 p-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatBRL(totals.subtotal)}</span>
                </div>
                {totals.descontoItens > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Descontos nos itens</span>
                    <span>- {formatBRL(totals.descontoItens)}</span>
                  </div>
                )}
                {totals.descontoCabecalho > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Desconto no total</span>
                    <span>- {formatBRL(totals.descontoCabecalho)}</span>
                  </div>
                )}
                {totals.frete > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Acréscimo / frete</span>
                    <span>+ {formatBRL(totals.frete)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-1 font-semibold">
                  <span>Total</span>
                  <span>{formatBRL(totals.total)}</span>
                </div>
              </div>
            )}
          </div>

          {/* PAGAMENTO, GARANTIA E CONDIÇÕES */}
          <Accordion type="single" collapsible>
            <AccordionItem value="pagamento" className={cn(CONTENT_CARD, 'overflow-hidden px-4')}>
              <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 shrink-0 text-primary" />
                  <span className="text-left">
                    Pagamento, garantia e condições
                    <span className="ml-1 font-normal text-muted-foreground">(opcional)</span>
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                <div className="space-y-2">
                  <FieldLabel>Formas de pagamento aceitas</FieldLabel>
                  <div className="flex flex-wrap gap-2">
                    {QUOTE_PAYMENT_METHODS.map((method) => {
                      const checked = (paymentMethods ?? []).includes(method);
                      return (
                        <button
                          key={method}
                          type="button"
                          // Chip é botão de ALTERNÂNCIA: sem `aria-pressed` quem
                          // usa leitor de tela não sabe se a forma está aceita.
                          aria-pressed={checked}
                          onClick={() =>
                            setPaymentMethods((prev) => {
                              const current = prev ?? [];
                              return current.includes(method)
                                ? current.filter((m) => m !== method)
                                : [...current, method];
                            })
                          }
                          className={cn(
                            'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                            checked
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-input bg-background text-muted-foreground hover:bg-accent',
                          )}
                        >
                          {PAYMENT_METHOD_LABELS[method]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="os-payment-terms">Condições de pagamento</FieldLabel>
                  <Input
                    id="os-payment-terms"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    placeholder="Ex.: 50% na entrada e 50% na entrega"
                    className={FIELD_INPUT}
                  />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="os-warranty">Garantia do serviço</FieldLabel>
                  <div className="flex gap-1.5">
                    <Input
                      id="os-warranty"
                      type="text"
                      inputMode="numeric"
                      value={warrantyAmount}
                      onChange={(e) => setWarrantyAmount(e.target.value)}
                      placeholder="Ex.: 90"
                      className={cn(FIELD_INPUT, 'flex-1')}
                    />
                    {/* Botões, não `Select`: em 360px os três rótulos cabem, e o
                        Select do Radix não é dirigível no jsdom (#145). */}
                    <div className="flex shrink-0 gap-1">
                      {WARRANTY_UNITS.map((unit) => (
                        <button
                          key={unit.value}
                          type="button"
                          aria-pressed={warrantyUnit === unit.value}
                          onClick={() => setWarrantyUnit(unit.value)}
                          className={cn(
                            'rounded-xl border px-2 text-xs font-medium transition-colors',
                            warrantyUnit === unit.value
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-input bg-background text-muted-foreground',
                          )}
                        >
                          {unit.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="os-terms">Termos e condições</FieldLabel>
                  <Textarea
                    id="os-terms"
                    value={termsConditions}
                    onChange={(e) => setTermsConditions(e.target.value)}
                    placeholder="Ex.: aparelho não retirado em 90 dias será cobrado armazenagem"
                    rows={3}
                    className="rounded-xl bg-background"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* RESPONSÁVEIS */}
          <Accordion type="single" collapsible>
            <AccordionItem value="responsaveis" className={cn(CONTENT_CARD, 'overflow-hidden px-4')}>
              <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <UserCog className="h-4 w-4 shrink-0 text-primary" />
                  <span className="text-left">
                    Responsáveis
                    <span className="ml-1 font-normal text-muted-foreground">(opcional)</span>
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="os-attendant">Atendente</FieldLabel>
                  <Input
                    id="os-attendant"
                    value={attendantName}
                    onChange={(e) => setAttendantName(e.target.value)}
                    placeholder="Quem abriu a ordem de serviço"
                    className={FIELD_INPUT}
                  />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="os-technician">Técnico responsável</FieldLabel>
                  <Input
                    id="os-technician"
                    value={technicianName}
                    onChange={(e) => setTechnicianName(e.target.value)}
                    placeholder="Quem executa o serviço"
                    className={FIELD_INPUT}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Barra FLUTUANTE — filha DIRETA do container da página (`sticky` só
            desliza dentro do próprio pai). */}
        <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
          <div className="flex gap-2">
            <Button3D
              color="gray"
              size="sm"
              className="flex-1"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancelar
            </Button3D>
            <Button3D size="sm" className="flex-1" onClick={handleSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Salvando…
                </>
              ) : isEditing ? (
                'Salvar'
              ) : (
                'Criar OS'
              )}
            </Button3D>
          </div>
        </div>
      </div>

      <ClientSelector open={showClientSelector} onOpenChange={setShowClientSelector} onSelect={handleSelectClient} />
      <CatalogSelector open={showCatalogSelector} onOpenChange={setShowCatalogSelector} onSelect={addFromCatalog} />
      <QuotePickerDialog
        open={showQuotePicker}
        onOpenChange={setShowQuotePicker}
        companyId={activeCompany?.id ?? null}
        onPick={(quote, quoteItems, quotePhotoRows) => {
          applyQuoteSeed(quote, quoteItems, quotePhotoRows);
          setShowQuotePicker(false);
        }}
      />
    </MainLayout>
  );
}

/**
 * HERO — o mesmo cabeçalho sangrado de `/quotes/new`.
 *
 * A OS é um DOCUMENTO com itens e total, então o valor ao vivo é o TOTAL (como
 * no orçamento) e não o nome — nos cadastros (cliente, catálogo) é o contrário.
 */
function ServiceOrderHero({
  isEditing,
  total,
  itemCount,
  clientName,
  status,
  onStatusChange,
  onBack,
}: {
  isEditing: boolean;
  total: number;
  itemCount: number;
  clientName: string;
  status: ServiceOrderStatus;
  onStatusChange: (status: ServiceOrderStatus) => void;
  onBack: () => void;
}) {
  const formatted = formatBRL(total);
  return (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={onBack}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            {isEditing ? 'Editando' : 'Nova'}
          </p>
          <h1 className="truncate text-lg font-bold leading-tight">Ordem de serviço</h1>
        </div>
      </div>

      <div className="relative mt-5">
        <p className="text-xs text-white/75">Total da ordem de serviço</p>
        <p className={cn('truncate font-bold leading-tight', heroTotalSizeClass(formatted))}>
          {formatted}
        </p>
        <p className="mt-0.5 truncate text-xs text-white/70">
          {[
            itemCount === 0 ? 'Nenhum item ainda' : plural(itemCount, 'item', 'itens'),
            clientName.trim() || null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      {/* Status: os 4 são um CONJUNTO e cabem na largura da tela — grade de 4
          colunas, com o rótulo curto (ver STATUS_PILL_LABELS). */}
      <div className="mt-4 grid grid-cols-4 gap-1.5">
        {SERVICE_ORDER_STATUSES.map((option) => {
          const active = option.value === status;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onStatusChange(option.value)}
              className={cn(
                'flex min-w-0 items-center justify-center gap-1 rounded-full px-1 py-1.5 text-[11px] font-medium transition-all',
                active
                  ? 'bg-white text-emerald-800 shadow-[inset_0_-3px_0_rgba(0,0,0,0.12)]'
                  : 'bg-white/15 text-white/85 hover:bg-white/25',
              )}
            >
              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_PILL_DOTS[option.value])} />
              <span className="truncate">{STATUS_PILL_LABELS[option.value]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
